<!-- eslint-disable vue/valid-v-slot -->
<template>
  <div class="company-page">
    <div class="page-header">
      <div class="page-header-text">
        <h1 class="page-title">Firmy</h1>
      </div>
    </div>
    <v-data-table
      :headers="headers"
      :items="companies"
      class="elevation-1"
      :loading="loading"
    >
      <template v-slot:top>
        <div class="table-section-title">
          <div class="filter-icon"><v-icon size="18" color="#5478FF">mdi-domain</v-icon></div>
          <div class="filter-title">{{ $t('company.title') }}</div>
          <v-spacer></v-spacer>
          <button class="action-pill action-pill--primary" @click="openCreateDialog">
            <v-icon left small>mdi-plus</v-icon>
            {{ $t('company.addCompany') }}
          </button>
        </div>
      </template>
      <template v-slot:item.details="{ item }">
        <v-btn class="warning" @click="infoCompany(item.id)"
          ><v-icon small class="pt-1">mdi-pencil</v-icon></v-btn
        >
      </template>
    </v-data-table>
    <!-- Form Dialog -->
    <v-dialog content-class="dark-form-dialog" v-model="dialog" max-width="600px">
      <v-card>
        <v-card-title>
          <span class="text-h6">{{ currentCompanyId ? $t('company.editCompany') : $t('company.newCompany') }}</span>
        </v-card-title>

        <v-card-text>
          <!-- Logo firmy -->
          <div class="company-logo-section mb-4">
            <div class="d-flex align-center mb-2">
              <v-icon left color="primary">mdi-image-outline</v-icon>
              <span class="text-subtitle-1 font-weight-bold">Logo firmy</span>
            </div>
            <v-row align="center">
              <v-col cols="12" sm="4">
                <div class="logo-preview">
                  <img v-if="formData.logoUrl" :src="formData.logoUrl" alt="Logo firmy" />
                  <div v-else class="logo-placeholder">
                    <v-icon size="42" color="grey">mdi-image-off-outline</v-icon>
                    <div class="caption grey--text mt-1">Brak loga</div>
                  </div>
                </div>
              </v-col>
              <v-col cols="12" sm="8">
                <v-file-input
                  v-model="logoFile"
                  label="Wybierz obraz (PNG/JPG, max 2 MB)"
                  accept="image/*"
                  outlined
                  dense
                  prepend-icon=""
                  prepend-inner-icon="mdi-upload"
                  show-size
                  :loading="logoUploading"
                  @change="onLogoPicked"
                />
                <div class="d-flex" style="gap: 8px;">
                  <v-btn
                    small
                    color="primary"
                    :disabled="!logoFile || logoUploading"
                    :loading="logoUploading"
                    @click="uploadLogo"
                  >
                    <v-icon small left>mdi-cloud-upload-outline</v-icon>
                    Zapisz logo
                  </v-btn>
                  <v-btn
                    v-if="formData.logoUrl"
                    small
                    outlined
                    color="error"
                    :loading="logoDeleting"
                    @click="removeLogo"
                  >
                    <v-icon small left>mdi-delete-outline</v-icon>
                    Usuń
                  </v-btn>
                </div>
              </v-col>
            </v-row>
            <v-divider class="mt-4" />
          </div>

          <v-form ref="form" v-model="valid" @submit.prevent="submitForm">
            <v-row>
              <!-- Name Field -->
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="formData.name"
                  :label="$t('common.name')"
                  :rules="[rules.required]"
                  required
                />
              </v-col>

              <!-- Address Line 1 -->
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="formData.addressLine1"
                  :label="$t('company.address')"
                  :rules="[rules.required]"
                  required
                />
              </v-col>

              <!-- Address Line 2 (Optional) -->
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="formData.addressLine2"
                  :label="$t('company.addressCont')"
                />
              </v-col>

              <!-- Zip Code -->
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="formData.zipCode"
                  :label="$t('company.zipCode')"
                  :rules="[rules.required]"
                  required
                />
              </v-col>

              <!-- City -->
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="formData.city"
                  :label="$t('company.city')"
                  :rules="[rules.required]"
                  required
                />
              </v-col>

              <!-- Country -->
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="formData.country"
                  :label="$t('company.country')"
                  :rules="[rules.required]"
                  required
                />
              </v-col>

              <!-- Tax Code (Optional) -->
              <v-col cols="12" md="6">
                <v-text-field v-model="formData.taxCode" :label="$t('company.taxCode')" />
              </v-col>

              <!-- Automatic Payouts Enabled -->
              <v-col cols="12" md="6">
                <v-switch
                  v-model="formData.automaticPayoutsEnabled"
                  :label="$t('company.automaticPayouts')"
                  disabled
                />
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <!-- Cancel Button -->
          <v-btn text color="secondary" @click="closeDialog()">{{ $t('common.cancel') }}</v-btn>
          <!-- Submit Button -->
          <v-btn color="primary" @click="submitForm" :disabled="!valid">
            {{ $t('common.save') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import Swal from "sweetalert2";
const axios = require("axios");
export default {
  data() {
    return {
      dialog: false,
      valid: false,
      loading: false,
      action: "",
      currentCompanyId: undefined,
      companies: [],
      logoFile: null,
      logoUploading: false,
      logoDeleting: false,
      headers: [
        {
          text: "Nazwa",
          align: "start",
          sortable: false,
          value: "name",
        },
        {
          text: "NIP",
          align: "start",
          sortable: false,
          value: "taxCode",
        },
        {
          text: "Adres",
          align: "start",
          sortable: false,
          value: "address",
        },
        {
          text: "Miasto",
          align: "start",
          sortable: false,
          value: "city",
        },
        { text: "", value: "details", sortable: false },
        { text: "", value: "deleteCompany", sortable: false },
      ],
      formData: {
        name: "",
        addressLine1: "",
        addressLine2: "",
        zipCode: "",
        city: "",
        country: "",
        taxCode: "",
        logoUrl: null,
        automaticPayoutsEnabled: false,
      },
      rules: {
        required: (value) => !!value || "Wymagane.",
      },
    };
  },
  computed: {
    isSuperAdmin() {
      const role = localStorage.getItem("role");
      const actualRole = localStorage.getItem("actualRole");
      const isHead = localStorage.getItem("isHead") === "true";
      return role === "superadmin" || actualRole === "headadmin" || isHead;
    },
  },

  async created() {
    await this.initialize();
  },
  methods: {
    openCreateDialog() {
      this.currentCompanyId = undefined;
      this.formData = {
        name: "",
        addressLine1: "",
        addressLine2: "",
        zipCode: "",
        city: "",
        country: "",
        taxCode: "",
        automaticPayoutsEnabled: false,
      };
      this.$refs.form?.resetValidation();
      this.dialog = true;
    },
    async toggleLoading(type) {
      const toggle = type;
      localStorage.setItem("loading", toggle);
      window.dispatchEvent(
        new CustomEvent("toggleLoading", {
          detail: {
            loading: localStorage.getItem("loading"),
            overlay: localStorage.getItem("loading"),
          },
        })
      );
    },
    async infoCompany(id) {
      await this.toggleLoading(true);
      try {
        const company = await axios({
          method: "get",
          url: process.env.VUE_APP_ROOT_API + "company/" + id,
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
        });
        this.formData = company.data.data;
        this.currentCompanyId = company.data.data.id;
        this.dialog = true;
        await this.toggleLoading(false);
      } catch (e) {
        await this.toggleLoading(false);
        console.log(e);
        Swal.fire("Błąd", e.response.data.message.toString(), "error");
      }
    },
    async submitForm() {
      // Check if form is valid
      if (this.$refs.form.validate()) {
        try {
          await this.toggleLoading(true);
          await axios({
            method: "post",
            url: process.env.VUE_APP_ROOT_API + "company",
            headers: {
              Authorization: "Bearer " + localStorage.getItem("token"),
            },
            data: {
              ...this.formData,
              id: this.currentCompanyId || undefined,
            },
          });
          this.closeDialog();
          await this.toggleLoading(false);
          await this.initialize();
        } catch (e) {
          await this.toggleLoading(false);
          console.log(e);
          Swal.fire("Błąd", e.response.data.message.toString(), "error");
        }
      }
    },
    closeDialog() {
      this.dialog = false;
      this.currentCompanyId = undefined;
      this.formData = {
        name: "",
        addressLine1: "",
        addressLine2: "",
        zipCode: "",
        city: "",
        country: "",
        taxCode: "",
        automaticPayoutsEnabled: false,
      };
      this.$refs.form?.resetValidation();
    },
    onLogoPicked(file) {
      if (!file) return;
      if (!file.type || !file.type.startsWith("image/")) {
        Swal.fire("Błąd", "Plik musi być obrazem", "error");
        this.logoFile = null;
        return;
      }
      if (file.size > 2 * 1024 * 1024) {
        Swal.fire("Błąd", "Plik za duży (max 2 MB)", "error");
        this.logoFile = null;
        return;
      }
      const reader = new FileReader();
      reader.onload = (e) => {
        this.formData = { ...this.formData, logoUrl: e.target.result };
      };
      reader.readAsDataURL(file);
    },
    async uploadLogo() {
      if (!this.logoFile) return;
      this.logoUploading = true;
      try {
        const fd = new FormData();
        fd.append("file", this.logoFile);
        const res = await axios({
          method: "post",
          url: process.env.VUE_APP_ROOT_API + "company/logo",
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
            "Content-Type": "multipart/form-data",
          },
          data: fd,
        });
        const url = res.data && res.data.data && res.data.data.logoUrl;
        if (url) this.formData = { ...this.formData, logoUrl: url };
        this.logoFile = null;
        Swal.fire({ icon: "success", title: "Logo zapisane", timer: 1500, showConfirmButton: false });
        await this.initialize();
      } catch (e) {
        const msg = e.response?.data?.message || "Nie udało się zapisać loga";
        Swal.fire("Błąd", msg.toString(), "error");
      }
      this.logoUploading = false;
    },
    async removeLogo() {
      const res = await Swal.fire({
        icon: "warning",
        title: "Usunąć logo firmy?",
        showCancelButton: true,
        confirmButtonText: "Tak, usuń",
        cancelButtonText: "Anuluj",
      });
      if (!res.isConfirmed) return;
      this.logoDeleting = true;
      try {
        await axios({
          method: "delete",
          url: process.env.VUE_APP_ROOT_API + "company/logo",
          headers: { Authorization: "Bearer " + localStorage.getItem("token") },
        });
        this.formData = { ...this.formData, logoUrl: null };
        Swal.fire({ icon: "success", title: "Logo usunięte", timer: 1500, showConfirmButton: false });
        await this.initialize();
      } catch (e) {
        const msg = e.response?.data?.message || "Nie udało się usunąć loga";
        Swal.fire("Błąd", msg.toString(), "error");
      }
      this.logoDeleting = false;
    },
    async initialize() {
      this.loading = true;
      try {
        const companies = await axios({
          method: "get",
          url: process.env.VUE_APP_ROOT_API + `company`,
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
        });
        this.companies = companies.data.data.map((user) => {
          const addressParts = [user.addressLine1, user.addressLine2, user.zipCode]
            .filter((p) => p && String(p).trim().length > 0);
          return {
            name: user.name,
            taxCode: user.taxCode || "—",
            address: addressParts.length ? addressParts.join(" ") : "—",
            city: user.city,
            id: user.id,
          };
        });
      } catch (e) {
        console.log(e);
        Swal.fire("Błąd", e.response.data.message.toString(), "error");
      }
      this.loading = false;
    },
  },
};
</script>

<style scoped>
.company-page {
  margin: -24px;
  padding: 24px;
  background: #181C28;
  min-height: calc(100vh - 64px);
  color: #E6EAF2;
}
.page-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  margin-bottom: 20px;
}
.page-eyebrow {
  font-size: 11px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #5478FF;
  font-weight: 600;
}
.page-title {
  font-size: 28px;
  font-weight: 700;
  margin: 4px 0 0 0;
  color: #FFFFFF;
}
.table-section-title {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 16px 20px;
  border-bottom: 1px solid #2A3142;
}
.filter-icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: rgba(84, 120, 255, 0.12);
  display: flex;
  align-items: center;
  justify-content: center;
}
.filter-title {
  font-size: 15px;
  font-weight: 600;
  color: #E6EAF2;
}
.action-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 600;
  border: 1px solid transparent;
  cursor: pointer;
  transition: all 0.15s ease;
}
.action-pill--primary {
  background: #5478FF;
  color: #FFFFFF;
}
.action-pill--primary:hover {
  background: #3F62E8;
}
.company-page >>> .v-data-table {
  background: #232838 !important;
  color: #E6EAF2 !important;
  border-radius: 12px;
  border: 1px solid #2A3142;
}
.company-page >>> .v-data-table thead tr th {
  background: #1F2433 !important;
  color: #94A0B8 !important;
  border-bottom: 1px solid #2A3142 !important;
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}
.company-page >>> .v-data-table tbody tr {
  background: transparent !important;
}
.company-page >>> .v-data-table tbody tr:hover {
  background: rgba(84, 120, 255, 0.06) !important;
}
.company-page >>> .v-data-table tbody tr td {
  border-bottom: 1px solid #2A3142 !important;
  color: #E6EAF2 !important;
}
.company-page >>> .v-data-footer {
  color: #94A0B8 !important;
  border-top: 1px solid #2A3142 !important;
}
.company-page >>> .v-dialog .v-card {
  background: #232838 !important;
  color: #E6EAF2 !important;
}
.company-page >>> .v-card__title,
.company-page >>> .v-card__text {
  color: #E6EAF2 !important;
}
.company-page >>> .v-text-field input,
.company-page >>> .v-text-field textarea {
  color: #E6EAF2 !important;
}
.company-page >>> .v-label {
  color: #94A0B8 !important;
}
.company-logo-section {
  background: #1F2433;
  border: 1px solid #2A3142;
  border-radius: 8px;
  padding: 14px 16px;
}
.logo-preview {
  background: #181C28;
  border: 1px solid #2A3142;
  border-radius: 6px;
  min-height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
}
.logo-preview img {
  max-width: 100%;
  max-height: 140px;
  object-fit: contain;
}
.logo-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  color: rgba(0, 0, 0, 0.4);
}
</style>
